<template>
  <div>
    <page-toolbar class="mb-3" :searchable="false">
      <template #filters>
        <v-col cols="6" sm="3">
          <dropdown
            v-model="filterPeriod"
            :items="periodOptions"
            item-text="label"
            item-value="value"
            :label="$t('finance_period_label')"
            color="white"
            hide-details
          />
        </v-col>
        <v-col cols="6" sm="3">
          <dropdown
            v-model="filterStatus"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            :label="$t('finance_status_label')"
            color="white"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="auto" class="d-flex align-center">
          <v-btn
            v-if="activeFilterCount > 0"
            text
            small
            color="primary"
            @click="resetFilters"
          >
            {{ $t("finance_reset_filters") }}
          </v-btn>
        </v-col>
      </template>
    </page-toolbar>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredPayouts"
        :loading="loading"
        :loading-text="$t('finance_loading_payouts')"
        mobile-breakpoint="0"
        :no-data-text="
          payouts.length > 0 ? $t('finance_no_payouts_filtered') : $t('finance_no_payouts')
        "
        @click:row="openPayout"
      >
        <template #item.date="{ item }">{{ localeDate(item.date) }}</template>
        <template #item.turnover="{ item }">{{ formatCurrency(item.turnover) }}</template>
        <template #item.deductions="{ item }">-{{ formatCurrency(item.deductions) }}</template>
        <template #item.net="{ item }">
          <span class="font-weight-bold">{{ formatCurrency(item.net) }}</span>
        </template>
        <template #item.status="{ item }">
          <v-chip small :color="PAYOUT_STATUS_META[item.status].color" dark>
            {{ $t(PAYOUT_STATUS_META[item.status].labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
      <p v-if="!loading && filteredPayouts.length > 0" class="hint-copy px-3 pb-3 mb-0">
        {{ $t("finance_click_payout_details_hint") }}
      </p>
    </v-card>

    <payout-detail-dialog v-model="detailOpen" :payout="selectedPayout" />
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";
import PayoutDetailDialog from "./dialogs/PayoutDetailDialog.vue";

export const PAYOUT_STATUS_META = {
  paid: { labelKey: "finance_status_payout_paid", color: "success" },
  pending: { labelKey: "finance_status_payout_pending", color: "warning" },
  processing: { labelKey: "finance_status_payout_processing", color: "secondary" },
};

const mapPayoutDto = (dto) => ({
  id: dto.id,
  date: dto.date,
  period: dto.period,
  turnover: Number(dto.turnover) || 0,
  deductions: Number(dto.deductions) || 0,
  net: Number(dto.net) || 0,
  status: dto.status,
  breakdown: {
    turnover: Number(dto.breakdown?.turnover) || 0,
    commission: Number(dto.breakdown?.commission) || 0,
    marketing: Number(dto.breakdown?.marketing) || 0,
    refunds: Number(dto.breakdown?.refunds) || 0,
    delivery: Number(dto.breakdown?.delivery) || 0,
    vat: Number(dto.breakdown?.vat) || 0,
    paidOut: Number(dto.breakdown?.paid_out) || 0,
  },
});

export default {
  name: "FinanceTabPayouts",

  components: { PayoutDetailDialog },

  mixins: [hasSnackbar],

  data: () => ({
    PAYOUT_STATUS_META,
    payouts: [],
    loading: true,
    filterPeriod: "all",
    filterStatus: null,
    detailOpen: false,
    selectedPayout: null,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_period_label"), value: "period" },
        { text: this.$t("finance_turnover"), value: "turnover" },
        { text: this.$t("finance_deductions"), value: "deductions" },
        { text: this.$t("finance_net"), value: "net" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },

    periodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
        { label: this.$t("finance_this_year"), value: "year" },
      ];
    },

    statusOptions() {
      return Object.keys(PAYOUT_STATUS_META).map((value) => ({
        value,
        label: this.$t(PAYOUT_STATUS_META[value].labelKey),
      }));
    },

    filteredPayouts() {
      return this.payouts.filter(
        (payout) =>
          this.isWithinPeriod(payout.date, this.filterPeriod) &&
          (!this.filterStatus || payout.status === this.filterStatus)
      );
    },

    activeFilterCount() {
      return (this.filterPeriod !== "all" ? 1 : 0) + (this.filterStatus ? 1 : 0);
    },
  },

  created() {
    this.loadPayouts();
  },

  methods: {
    async loadPayouts() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/payouts`);
        this.payouts = (data?.data || []).map(mapPayoutDto);
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_payouts_load_error")
        );
      } finally {
        this.loading = false;
      }
    },

    isWithinPeriod(date, value) {
      if (value === "all") return true;

      const target = new Date(date);
      const now = new Date();

      if (value === "year") return target.getFullYear() === now.getFullYear();

      const days = value === "30d" ? 30 : 90;
      return now.getTime() - target.getTime() <= days * 24 * 60 * 60 * 1000;
    },

    resetFilters() {
      this.filterPeriod = "all";
      this.filterStatus = null;
    },

    openPayout(payout) {
      this.selectedPayout = payout;
      this.detailOpen = true;
    },

    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return `${value.toFixed(2)} ${this.restaurantCurrency}`;
    },
  },
};
</script>

<style scoped>
.hint-copy {
  font-size: 0.78rem;
  color: #9aa4b2;
}

::v-deep tbody tr {
  cursor: pointer;
}
</style>
